import * as React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { Trip } from 'contracts/tracking-v2/trips';
import {
  TripDeliveryStatusIcon,
  TripInvoiceNumbersHoverCard,
} from 'pages/tracking-v2/shared-components';
import { getTripDeliveryInfo } from 'pages/tracking-v2/utils/trip-utils';
import { DataTableHelper, Formatter } from 'utils';

const dt = new DataTableHelper<Trip>();

export const useTripColumns = (): ColumnDef<Trip>[] => {
  const id = dt.buildColumn({
    id: 'Id',
    accessorKey: 'id',
    size: 64,
    content: (row) => row.id,
  });
  const cargoIdentifier = dt.buildColumn({
    id: 'Id da carga',
    accessorKey: 'cargoIdentifier',
    content: (row) => row.cargoIdentifier,
  });
  const nfsDetails = dt.buildColumn({
    id: 'NFs',
    size: 64,
    enableSorting: false,
    enableHiding: false,
    accessorFn: (row) => row.invoices.length,
    content: (row) => (
      <div className={'will-change-contents max-w-max'}>
        <TripInvoiceNumbersHoverCard
          invoices={row.invoices}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    ),
  });
  const deadlineDate = dt.buildColumn({
    id: 'Prazo',
    accessorKey: 'deadlineDate',
    size: 64,
    content: (row) => row.deadlineDate ? Formatter.dateFull(row.deadlineDate) : '---',
  });
  const deliveredCount = dt.buildColumn({
    id: 'Entregues',
    size: 64,
    enableSorting: false,
    enableHiding: false,
    accessorFn: (row) => getTripDeliveryInfo(row.invoices).deliveredCount,
    content: (row) => {
      const { deliveredCount, invoicesCount } = getTripDeliveryInfo(row.invoices);
      return (
        <span>
          {deliveredCount}/{invoicesCount}
        </span>
      );
    },
  });
  const supplier = dt.buildColumn({
    id: 'Fornecedor',
    accessorFn: (row) => row.supplier.id,
    content: (row) => row?.supplier?.tradeName || '---',
  });
  const supplierLocale = dt.buildColumn({
    id: 'Origem',
    accessorFn: (row) => row.supplier.id,
    content: (row) => {
      const { addressCity, addressUf } = row.supplier;
      return [addressCity, addressUf].filter(Boolean).join('/');
    },
  });
    const vehiclePlate = dt.buildColumn({
      id: 'Placa do Veiculo',
      accessorKey: 'vehiclePlate',
      content: (row) => row.vehiclePlate,
    });
    const destinationCity = dt.buildColumn({
      id: 'Destino',
      accessorKey: 'vehiclePlate',
      content: (row) =>
        `${row.invoices[0]?.destinationCity?.name}, ${row.invoices[0]?.destinationCity?.state} `,
    });
  const carrier = dt.buildColumn({
    id: 'Transportadora',
    accessorFn: (row) => row.carrier.id,
    content: (row) => row?.carrier?.tradeName || '---',
  });
  const deliveryStatus = dt.buildColumn({
    id: 'deliveryStatus',
    title: '',
    enableHiding: false,
    enableSorting: false,
    accessorFn: (row) => getTripDeliveryInfo(row.invoices).deliveredRatio,
    content: (row) => (
      <div className={'flex items-center justify-end'}>
        <TripDeliveryStatusIcon invoices={row.invoices} />
      </div>
    ),
  });

  return [
    id,
    cargoIdentifier,
    nfsDetails,
    deadlineDate,
    deliveredCount,
    supplier,
    supplierLocale,
    destinationCity,
    carrier,
    vehiclePlate,
    deliveryStatus,
  ];
};